import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  flashAnchorWhenReady,
  scrollToAnchorWhenReady,
} from '../helpers/scroll-to-project.util';

import { AppService } from '../services/app.service';
import {
  AssociateSiteVisitQueueRow,
  SiteVisitService,
  SiteVisitView,
} from '../services/site-visit.service';
import { SiteVisitNegotiationComponent } from '../site-visit/site-visit-negotiation/site-visit-negotiation.component';
import { isTerminalStatus } from '../site-visit/site-visit-state.util';
import { createTestIdScope, slugify } from '../shared/testid/testid.util';
import {
  QUEUE_ROW_FLASH_CLASS,
  SchedulingQueueRowComponent,
  siteVisitAnchorId,
} from './scheduling-queue-row/scheduling-queue-row.component';
import {
  SCHEDULING_QUEUE_GROUP_HINT,
  SCHEDULING_QUEUE_GROUP_ORDER,
  SCHEDULING_QUEUE_GROUP_TITLE,
  SchedulingQueueGroupVm,
  SchedulingQueueRowVm,
  buildQueueRowVm,
  compareQueueRows,
  pendingActionsFor,
  queueGroupOf,
  reconcileRow,
} from './scheduling-queue.util';

/**
 * The professional associate's cross-project scheduling queue.
 *
 * CONNECT 1.5 has one page listing every live site visit a vendor holds, with the next action on each
 * row; 2.0 had the same components but only per project, so a PA with visits on six projects had six
 * places to look and no way to know which needed attention. This is that page — a work queue, not a
 * table, ordered by **who owes the next move**.
 *
 * <h2>The queue supplements the per-project area; it does not replace it (addendum §5.1)</h2>
 * Two surfaces therefore perform the same transitions, which makes three things requirements:
 *  - **One mapping.** Group, label and pending action are all derived from `site-visit-state.util.ts`,
 *    the port of connect-service's `SiteVisitTransitionEnum`. Nothing in this feature carries a second
 *    state map.
 *  - **One set of components.** Every state's interaction is {@link SiteVisitNegotiationComponent},
 *    embedded in the opened row. Nothing here re-implements a state's form: a re-implementation would
 *    mean every future state change has to land in two places, and the first one someone forgets is a
 *    silent divergence. The row itself is {@link SchedulingQueueRowComponent} for the same reason — this
 *    page renders it, and so does the workspace's site-visit card.
 *  - **Refetch on entry, and no attempt to be live.** The PA can act in the project view or in another
 *    tab, so rows go stale. This is a top-level route, so the component is destroyed and recreated on
 *    each entry and `ngOnInit` *is* the refetch — the same pattern the Available Work feed uses. In-place
 *    commit after the PA's own action is kept for the same reason it was kept there: rebuilding the list
 *    would close the row they are working in.
 *
 * <h2>🔴 Opening a row fetches the detail. It is never rendered from the list.</h2>
 * A visit resting in `REJECTED_ALTERNATIVES_PROVIDED` is parked awaiting a `SYSTEM` advance, and there
 * is no scheduler: `advanceStuckVisit` applies it lazily on the **detail** path, not on the queue read,
 * which is deliberately side-effect-free. So {@link open} always calls
 * `GET /site-visit/project/{projectId}/site/{siteNumber}` with the row's own key, and that call is what
 * heals the parked visit. Rendering the detail from data already in the list would make such a visit
 * permanently unhealable, which is the exact bug that including the state in the queue was meant to
 * close.
 *
 * <h2>`?visitId=` — the associate notification email's landing (addendum Task 20)</h2>
 * connect-service's `SiteVisitEmailHandler` points the PA mail's `returnUrl` here, deep-linked to the visit
 * the mail is about. Landing on a queue of nine when the mail was about one visit is the failure that link
 * exists to avoid, so on arrival the matching row is opened and scrolled to.
 *
 * It goes through {@link open}, not a copy of it, which is what makes the deep link *heal* as well as
 * land: the detail read is the only thing that advances a parked visit, so a mail about a visit whose
 * `SYSTEM` advance failed lands, opens, and self-corrects. An id that matches no row — malformed, stale, or
 * simply not this PA's, since the feed is principal-scoped — does nothing at all, leaving the plain queue.
 * The id is not a credential and is not treated as one: it selects from a feed that was already scoped
 * server-side, so it can only ever reveal something the PA could already see.
 *
 * The parameter carries a **comma-separated list**, because signing one ATP can open a visit on every site
 * of a multi-site project and the post-signature offer links to all of them at once. Only the first match
 * is opened and scrolled to — there is one viewport and one open row, so a second scroll would simply undo
 * the first — and the rest are flashed where they sit. See {@link applyDeepLink}.
 */
@Component({
  selector: 'app-scheduling',
  standalone: true,
  imports: [RouterModule, SchedulingQueueRowComponent, SiteVisitNegotiationComponent],
  templateUrl: './scheduling-page.component.html',
  styleUrl: './scheduling-page.component.scss',
})
export class SchedulingPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /** Every live visit, in wire order plus what was derived from each. */
  rowVms: SchedulingQueueRowVm[] = [];

  /** The three groups, always all three, in the approved order. */
  groups: SchedulingQueueGroupVm[] = [];

  loading = true;
  loadFailed = false;

  /** The visit whose row is expanded, by id — so a row that changes group stays open across a regroup. */
  openVisitId: string | null = null;

  /** The opened row's detail, fetched fresh. Never assembled from the list. */
  detail: SiteVisitView | null = null;
  detailLoading = false;
  detailFailed = false;

  /**
   * Whether the open row was opened by its *Reschedule* button — bound straight through to the panel's
   * `startRescheduleExpanded`, so the form the PA named is already on screen.
   *
   * 🔴 **Single-use, and every path that ends an open clears it.** The row's Reschedule control is the
   * only thing that sets it; {@link open} clears it at the top of every generic open, {@link close}
   * clears it, and {@link onVisitChange} clears it once a move has been accepted. That is what makes
   * *View Details* on the same row come back collapsed: the second open re-binds this `false` and the
   * panel — rebuilt fresh under the row's `@if` — reads `false` in its `ngOnInit`. A flag that was only
   * ever set would be indistinguishable from "this row reschedules forever".
   *
   * The `onVisitChange` clear is the least obvious of the three and the one that matters most: an
   * accepted reschedule can move the row to another group, which rebuilds the row and its projected
   * panel without either {@link open} or {@link close} running. `applySchedule` has just wiped the
   * form's four fields, so a stale `true` would re-expand an EMPTIED form and present it as mid-edit —
   * exactly the state that method's own comment exists to prevent.
   */
  rescheduleIntent = false;

  /**
   * The visits the associate email asked for, from `?visitId=` — consumed once, on the first build, then
   * emptied. One-shot so an in-place commit that regroups the list does not re-open, re-scroll or re-flash
   * a row the PA has since closed.
   *
   * 🔴 The **whole list** is emptied on the first read, before any of it is matched — never per id, and
   * never after matching. Clearing as we go would leave a partially-consumed list behind for a later build
   * to finish applying, which is the same stale-reveal this field is nulled up front to prevent, only
   * harder to see.
   */
  private deepLinkVisitIds: string[] = [];

  /** Stops the scroll-and-flash frame loop if the page is torn down before the row's anchor renders. */
  private destroyed = false;

  constructor(
    private siteVisitService: SiteVisitService,
    private route: ActivatedRoute,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    // Read before fetch(): a synchronous emission from the service would otherwise reach build() with no
    // deep-link target recorded yet.
    this.deepLinkVisitIds = parseDeepLinkVisitIds(
      this.route.snapshot.queryParamMap.get('visitId')
    );
    this.fetch();
    // Cleared after the read above, not before: this page is the capture path's destination (Task 20),
    // so a login reached with neither a `?returnUrl=` nor an AuthGuard bounce would otherwise inherit
    // whatever this BehaviorSubject last held.
    this.appService.setRedirectUrl('');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- the queue read ---------------------------------------------------------------------------

  /**
   * The one queue read. Fires once per entry into the route and never again — there is deliberately no
   * polling and no router subscription on top of `ngOnInit`, which would turn one request into two.
   */
  private fetch(): void {
    this.loading = true;
    this.loadFailed = false;
    this.siteVisitService
      .getSiteVisitQueue()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response?.status === 'FAILURE') {
            this.loadFailed = true;
            this.build([]);
            return;
          }
          this.build(response?.data ?? []);
        },
        error: () => {
          this.loading = false;
          this.loadFailed = true;
          this.build([]);
        },
      });
  }

  /** Build the row view-models from the wire rows, then group them. */
  private build(rows: AssociateSiteVisitQueueRow[]): void {
    const scope = createTestIdScope();
    this.rowVms = (rows ?? [])
      .filter((row) => !!row)
      .map((row) => buildQueueRowVm(row, scope));
    this.regroup();
    this.applyDeepLink();
  }

  /**
   * Open and reveal the rows the associate email named, once the queue has been built.
   *
   * 🔴 Routed through {@link open} rather than setting `openVisitId` here, because opening is what fetches
   * the detail — and the detail read is what heals a visit parked awaiting a `SYSTEM` advance. A deep link
   * that expanded the row without that call would land the PA on exactly the visit the mail was about and
   * show them a stale state nothing would ever correct.
   *
   * A miss is silent and deliberate: an id that is malformed, belongs to a settled visit, or belongs to
   * another PA is simply not in this principal-scoped feed, and the plain queue is the honest answer. There
   * is nothing here to tell apart — and no message to write — because the id was never a claim to anything.
   * With several ids that also settles the partial case: three named, two found, two marked, no message —
   * the two that were not found were no more a claim to anything than a lone miss is.
   *
   * 🔴 **Only the first match opens.** Opening is single-slot (`openVisitId`, one `detail`) and there is one
   * viewport, so opening the rest would leave only the last one open and scrolling to the rest would undo
   * the first scroll — the PA would be dropped somewhere they did not ask to be, with the row the mail led
   * with closed. The others get {@link flashAnchorWhenReady} instead: marked in place, findable, and left
   * for the PA to open in whatever order they choose to work.
   *
   * The list is emptied at the top, before a single id is looked up — see {@link deepLinkVisitIds}.
   */
  private applyDeepLink(): void {
    const visitIds = this.deepLinkVisitIds;
    this.deepLinkVisitIds = []; // one-shot, all of it, whether or not any of it matched anything
    if (!visitIds.length) {
      return;
    }
    let opened = false;
    for (const visitId of visitIds) {
      const vm = this.rowVms.find((candidate) => candidate.row.id === visitId);
      if (!vm) {
        continue;
      }
      if (!opened) {
        opened = true;
        this.open(vm);
        scrollToAnchorWhenReady(this.anchorId(vm), QUEUE_ROW_FLASH_CLASS, () => this.destroyed);
        continue;
      }
      flashAnchorWhenReady(this.anchorId(vm), QUEUE_ROW_FLASH_CLASS, () => this.destroyed);
    }
  }

  /**
   * The row's DOM anchor — what the `?visitId=` deep link reveals.
   *
   * 🔴 Delegated to {@link siteVisitAnchorId} rather than composed here, because the element carrying the
   * id is now rendered by {@link SchedulingQueueRowComponent} and `scrollToAnchorWhenReady` finds it by
   * `document.getElementById`. Two literals would fail silently: every other assertion about the row
   * stays green and the associate simply lands at the top of the queue the mail existed to skip.
   */
  anchorId(vm: SchedulingQueueRowVm): string {
    return siteVisitAnchorId(vm.row.id);
  }

  /** Partition the rows into the three groups, each sorted by operative date. */
  private regroup(): void {
    this.groups = SCHEDULING_QUEUE_GROUP_ORDER.map((key) => {
      const rows = this.rowVms
        .filter((vm) => vm.group === key)
        .sort((a, b) => compareQueueRows(a.row, b.row));
      return {
        key,
        title: SCHEDULING_QUEUE_GROUP_TITLE[key],
        hint: SCHEDULING_QUEUE_GROUP_HINT[key],
        testId: `scheduling-group-${slugify(key)}`,
        count: rows.filter((vm) => !vm.settled).length,
        rows,
      };
    });
  }

  // --- empty states -----------------------------------------------------------------------------

  get hasRows(): boolean {
    return this.rowVms.length > 0;
  }

  /**
   * The count that gives *Awaiting you* its emphasis — the reason the page exists.
   *
   * Settled rows are excluded even though they keep their place in the group: a visit the PA has just
   * completed is pinned there so what they did stays legible, but it is not something still awaiting
   * them, and a header reading "1" over nothing to do is the one thing this count must not do.
   */
  get awaitingCount(): number {
    return this.rowVms.filter((vm) => vm.group === 'AWAITING_YOU' && !vm.settled)
      .length;
  }

  /**
   * There are visits, but none of them need the PA. A different message from "no visits at all", because
   * the ordering makes the distinction visible: an empty first group with rows below it is the good
   * outcome, not an empty page.
   */
  get nothingAwaitingYou(): boolean {
    return this.hasRows && this.awaitingCount === 0;
  }

  // --- row rendering ------------------------------------------------------------------------------

  // 🔴 The row's own rendering — label, pill, site, day, window, time and action wording — belongs to
  // {@link SchedulingQueueRowComponent}, so the queue and the workspace's site-visit card cannot come to
  // describe the same visit differently. Nothing may be re-derived here.

  trackByVisitId(_index: number, vm: SchedulingQueueRowVm): string {
    return vm.row.id;
  }

  trackByGroupKey(_index: number, group: SchedulingQueueGroupVm): string {
    return group.key;
  }

  isOpen(vm: SchedulingQueueRowVm): boolean {
    return this.openVisitId === vm.row.id;
  }

  // --- opening a row ------------------------------------------------------------------------------

  /**
   * Expand a row, or collapse it if it is already the open one.
   *
   * 🔴 Expanding always fetches the detail. See the class comment for why this may not be served from
   * the list — a parked `REJECTED_ALTERNATIVES_PROVIDED` visit is healed by this call and by nothing
   * else the queue does.
   */
  open(vm: SchedulingQueueRowVm): void {
    if (this.isOpen(vm)) {
      this.close();
      return;
    }
    // A generic open — View Details, the pending action, a deep link. Cleared here rather than only in
    // `close()` because a PA can go straight from one open row to another without closing the first.
    this.rescheduleIntent = false;
    this.openVisitId = vm.row.id;
    this.detail = null;
    this.detailFailed = false;

    const projectId = vm.row.projectId;
    const siteNumber = vm.row.siteNumber;
    if (!projectId || siteNumber === null || siteNumber === undefined) {
      // Without the key there is no detail to fetch. The row still expands, so the deep link out to the
      // project overview is reachable rather than the click doing nothing.
      this.detailFailed = true;
      return;
    }

    this.detailLoading = true;
    this.siteVisitService
      .getSiteVisit(projectId, siteNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // The PA opened another row while this was in flight. `detail` is a singleton, so writing to
          // it now would render this visit's negotiation — and at TIME_CONFIRMED this visit's site
          // contact — underneath the other row's header.
          if (this.openVisitId !== vm.row.id) {
            return;
          }
          this.detailLoading = false;
          const view = response?.status === 'FAILURE' ? null : response?.data ?? null;
          if (!view) {
            this.detailFailed = true;
            return;
          }
          this.detail = view;
          // The read may have advanced a parked visit. Fold the answer back so the row's label, group
          // and pending action follow the state the PA is now looking at.
          this.commit(vm.row.id, view);
        },
        error: () => {
          if (this.openVisitId !== vm.row.id) {
            return;
          }
          this.detailLoading = false;
          this.detailFailed = true;
        },
      });
  }

  /**
   * Open the row AND record that the PA asked for the reschedule form by name, so the panel opens with
   * that section already unfolded instead of collapsed behind its disclosure.
   *
   * 🔴 The flag is set from {@link isOpen} rather than to a bare `true`, because {@link open} is a
   * TOGGLE: handed the row that is already open it closes it instead, and asserting intent over a row
   * that just closed would leave a stale `true` for whatever opens next. Reading back what actually
   * happened is correct for every case without a second branch here. (The row only renders its
   * Reschedule control while closed, so the toggle-shut path is unreachable from the button today —
   * this is written not to depend on that.)
   */
  openForReschedule(vm: SchedulingQueueRowVm): void {
    this.open(vm);
    this.rescheduleIntent = this.isOpen(vm);
  }

  close(): void {
    this.openVisitId = null;
    this.detail = null;
    this.detailLoading = false;
    this.detailFailed = false;
    this.rescheduleIntent = false;
  }

  /** The negotiation moved under the PA's own hand — same fold-back path as the detail read. */
  onVisitChange(view: SiteVisitView): void {
    this.detail = view;
    // The intent is spent: it belonged to the open it caused, and that open has now produced a move.
    // A commit can regroup the row, which rebuilds the row and its panel with neither `open` nor
    // `close` running — see {@link rescheduleIntent} for why a stale `true` would then re-expand a form
    // the panel has just emptied.
    this.rescheduleIntent = false;
    if (this.openVisitId) {
      this.commit(this.openVisitId, view);
    }
  }

  /**
   * Adopt a freshly-read negotiation onto its row **in place**, and re-derive what depends on it.
   *
   * In place, rather than refetching the queue, for the reason the Available Work feed keeps the same
   * behavior: rebuilding the list closes the row the PA is working in.
   *
   * A visit the PA has just completed or cancelled is no longer live work and the next entry to the page
   * will not return it — but it is not removed from under them mid-glance. It keeps its position and its
   * group, marked {@link SchedulingQueueRowVm.settled}, so what they did stays legible; re-deriving its
   * group would move a completed visit into *Waiting on others*, which is nobody's truth.
   */
  private commit(visitId: string, view: SiteVisitView): void {
    const vm = this.rowVms.find((candidate) => candidate.row.id === visitId);
    if (!vm) {
      return;
    }
    vm.row = reconcileRow(vm.row, view);
    if (isTerminalStatus(vm.row.status)) {
      vm.settled = true;
      vm.pending = [];
      // Its group is deliberately NOT re-derived, so it stays where the PA left it — but the counts are,
      // so the header stops claiming a completed visit is outstanding work.
      this.regroup();
      return;
    }
    vm.settled = false;
    vm.pending = pendingActionsFor(vm.row);
    vm.group = queueGroupOf(vm.row);
    this.regroup();
  }
}

/**
 * Split the `?visitId=` parameter into the ids it names.
 *
 * One id is the overwhelmingly common case and stays exactly what it always was: a single-element list
 * whose one entry is opened and scrolled to. Several arrive from the post-signature offer, which joins the
 * ids of every visit that signature opened.
 *
 * Blank and whitespace-only entries are dropped rather than kept as empty ids, so a trailing comma or a
 * hand-edited URL costs nothing: an empty id could never match a row anyway, but keeping it would let it
 * consume the "first match" slot the moment anything about matching changed.
 */
function parseDeepLinkVisitIds(param: string | null): string[] {
  if (!param) {
    return [];
  }
  return param
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}
